import { Component, OnInit, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { StringHelper } from 'src/app/shared/utils/string-helper';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { IKeyValuePair } from 'src/app/shared/services/rest/base.service';
import { LanguageService } from 'src/app/shared/services/language.service';
import { AppConfigService } from 'src/app/shared/services/app-config.service';

@Component({
  selector: 'app-main-search-input',
  templateUrl: './main-search-input.component.html'
})
export class MainSearchInputComponent implements OnInit {

  /**
   * Angular Form model
   *
   * @type {FormGroup}
   * @memberof MainSearchInputComponent
   */
  @Input() searchForm: FormGroup;

  @Input() numberCaption: string;

  @Input() numberSubCaption: string;

  @Input() checkboxCaption: string;

  /**
   * time dropdown - key: minutes
   *
   * @type {IKeyValuePair<number, string>[]}
   * @memberof MainSearchInputComponent
   */
  startTimes: IKeyValuePair<number, string>[] = [];

  endTimes: IKeyValuePair<number, string>[] = [];

  timeSpanDropdown: IKeyValuePair<number, string>[] = [];

  dateFormat: string;

  durationInHours: string;

  durationInHoursChecked: boolean;

  constructor(private language: LanguageService) {
    this.initTimeSpanDropdown();
    this.initAbsoluteTimeDropdown();
  }

  ngOnInit() {
    this.ensureProperty('searchForm');
    this.ensureProperty('numberCaption');
    this.ensureProperty('numberSubCaption');
    this.ensureProperty('checkboxCaption');

    this.language.getDateFormat().then((val) => {
      this.dateFormat = val;
    });
  }

  private ensureProperty(property: keyof MainSearchInputComponent) {
    if (this[property] === undefined) {
      throw Error('the property ' + property + ' is required on MainSearchInputComponent');
    }
  }

  /**
   * Init dropdown to select a duration (gap) to find
   *
   * @private
   * @memberof MainSearchInputComponent
   */
  private initTimeSpanDropdown() {

    const defaultTimePeriod = AppConfigService.settings.DefaultTimePeriod;

    const timeSpanDropdown = [];
    for (let i = 2; i <= 40; i++) {
      if (i < 8) {
        // von 0: 30 bis 2: 00 in Schritten von 15 Minuten
        timeSpanDropdown.push({ key: (60 * i / 4), value: this.createTime(i, 4) });
        continue;
      }
      if (i < 16) {
        if (i % 2 === 0) {
          // von 2: 00 bis 4: 00 in Schritten von 30 Minuten
          timeSpanDropdown.push({ key: (60 * i / 4), value: this.createTime(i, 4) });
        }
        continue;
      }
      if (i % 4 === 0) {
        // von 4: 00 bis 10: 00 in Schritten von 1 Stunde
        timeSpanDropdown.push({ key: (60 * i / 4), value: StringHelper.formatTime(i / 4) });
      }
    }

    // include defined default time period if not in the list
    if (timeSpanDropdown.findIndex(ts => ts.key === defaultTimePeriod) === -1) {
      timeSpanDropdown.push({ key: defaultTimePeriod, value: DateHelper.createDisplayTime(defaultTimePeriod) });
      timeSpanDropdown.sort((tsl, tsr) => tsl.key - tsr.key);
    }

    // assign generated drop down list
    this.timeSpanDropdown = this.timeSpanDropdown.concat(timeSpanDropdown);
  }

  /**
  * Init dropdown to select time from - to
  *
  * @private
  * @memberof MainSearchInputComponent
  */
  private initAbsoluteTimeDropdown() {
    const defaultTimeFragmentation = AppConfigService.settings.DefaultTimeFragmentation;
    const FALLBACK_FRAGMENTATION_PERIOD_VALUE = 15;

    // if number does not fit well into an hour, discard and use fallback fragmentation time instead
    const validTimeFragmentation = Number.isInteger(60 / defaultTimeFragmentation)
      ? defaultTimeFragmentation
      : FALLBACK_FRAGMENTATION_PERIOD_VALUE;

    if (validTimeFragmentation !== defaultTimeFragmentation) {
      console.warn('60 is not a factor of ' + defaultTimeFragmentation + ', the Configuration Entry value `DefaultTimeFragmentation`. The default value of `15` will be used instead');
    }

    const defaultTimePeriod = AppConfigService.settings.DefaultTimePeriod;
    const timeSlots = Math.ceil((60 * 24) / validTimeFragmentation);
    const startTimesList: IKeyValuePair<number, string>[] = Array(timeSlots);

    for (let i = 0; i < timeSlots; i++) {
      const minPastMidnight = i * validTimeFragmentation;
      startTimesList[i] = { key: minPastMidnight, value: DateHelper.createDisplayTime(minPastMidnight) };
    }
    const endTimesList = startTimesList.map(timeslot => ({ key: timeslot.key + defaultTimePeriod, value: DateHelper.createDisplayTime(timeslot.key + defaultTimePeriod) }));

    // assign generated drop down list
    this.startTimes = startTimesList;
    this.endTimes = endTimesList;
  }

  private createTime(num: number, base: number) {
    const minutes = (num % base) * (60 / base);
    return StringHelper.formatTime(Math.floor(num / base), minutes);
  }

}
